<!--
  StoreCloseDateCard -- the scoped store's close date on the portal Overview,
  with "Extend close date" (card 'Rename "Admin Buyer" to Client Portal and
  retire the legacy Admin Buyer dashboard', slice 0: portal parity).

  The legacy Admin Buyer dashboard's "Days Until Close" tile and its "Select
  New Close Date" dialog, moved into the portal. Everything it shows comes
  from ONE row: the /api/portal/stores entry for the scoped store
  (`close_date`, `days_until_close`, `has_closed`, `is_permanent_store`,
  `complete`, `can_extend_close_date`, `extend_blocked_reason`). The server
  decides whether the store can be extended -- the same rule its write
  enforces -- and says why not, so this card never re-derives eligibility
  from role or flags.

  The confirm POSTs /api/portal/stores/<id>/end-date and emits `updated` with
  the refreshed row the server returns; the page hands it to the scope store
  so the countdown re-renders from the server's answer, not a local guess.
-->
<template>
  <v-card class="pa-4" data-testid="overview-close-date-card">
    <div class="close-date-card">
      <div class="close-date-card__main">
        <div class="text-caption text-medium-emphasis">Store close date</div>

        <template v-if="store.is_permanent_store">
          <div class="text-h6 mt-1" data-testid="overview-close-date-permanent">
            No close date
          </div>
          <div class="text-caption text-medium-emphasis mt-1">
            This is a permanent store, so it stays open.
          </div>
        </template>

        <template v-else-if="store.close_date">
          <div class="d-flex align-baseline mt-1" style="gap: 8px">
            <span class="text-h4" data-testid="overview-days-until-close">{{
              store.days_until_close
            }}</span>
            <span class="text-body-2 text-medium-emphasis">{{ daysLabel }}</span>
          </div>
          <div class="text-body-2 mt-1">
            {{ store.has_closed ? "Closed" : "Closes" }}
            <strong data-testid="overview-close-date-value">{{
              formatDate(store.close_date)
            }}</strong>
          </div>
          <div
            v-if="store.has_closed"
            class="text-caption text-medium-emphasis mt-1"
            data-testid="overview-close-date-closed"
          >
            This store has closed.
          </div>
        </template>

        <div
          v-else
          class="text-body-2 text-medium-emphasis mt-1"
          data-testid="overview-close-date-none"
        >
          No close date is set for this store.
        </div>

        <div
          v-if="store.complete"
          class="text-caption mt-2 close-date-card__locked"
          data-testid="overview-close-date-locked"
        >
          <v-icon size="16" icon="lock" /> Editing locked: this store is complete.
        </div>
      </div>

      <!-- Offered to a caller who may edit close dates: enabled when the
           server says the store can be extended, otherwise DISABLED with the
           server's reason (closed, permanent, complete, archived) so the
           refusal is never silent. A caller with no edit right (Store
           Manager) gets neither: the row carries no reason for them. -->
      <div v-if="actionShown" class="close-date-card__action">
        <PortalButton
          variant="secondary"
          :disabled="!store.can_extend_close_date"
          data-testid="overview-extend-close-date"
          @click="openDialog"
        >
          <template #icon><v-icon size="18" icon="calendar_month" /></template>
          Extend close date
        </PortalButton>
        <div
          v-if="!store.can_extend_close_date"
          class="text-caption mt-1 close-date-card__blocked"
          data-testid="overview-extend-blocked-reason"
        >
          {{ store.extend_blocked_reason }}
        </div>
      </div>
    </div>

    <div
      v-if="successMsg"
      class="text-body-2 mt-3 close-date-card__success"
      data-testid="overview-extend-success"
      role="status"
    >
      {{ successMsg }}
    </div>

    <v-dialog v-model="dialogOpen" max-width="400" data-testid="overview-extend-dialog">
      <v-card class="pa-5">
        <div class="text-subtitle-1 mb-1">Extend close date</div>
        <div class="text-body-2 text-medium-emphasis mb-3">
          Pick a new close date for <strong>{{ store.name }}</strong>. We will
          let your store's contact know.
        </div>
        <v-text-field
          v-model="newDate"
          label="New close date"
          type="date"
          :min="minDate"
          variant="outlined"
          density="compact"
          data-testid="overview-extend-date-input"
        />
        <div
          v-if="errorMsg"
          class="text-body-2 mb-2 close-date-card__error"
          data-testid="overview-extend-error"
          role="alert"
        >
          {{ errorMsg }}
        </div>
        <div class="d-flex justify-end mt-2" style="gap: 8px">
          <PortalButton
            variant="ghost"
            data-testid="overview-extend-cancel"
            @click="dialogOpen = false"
          >
            Cancel
          </PortalButton>
          <PortalButton
            :loading="saving"
            :disabled="!newDate"
            data-testid="overview-extend-confirm"
            @click="confirm"
          >
            Confirm close date
          </PortalButton>
        </div>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from "@/axios";
import PortalButton from "@/components/portal/PortalButton.vue";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// "YYYY-MM-DD" for a local Date (the date input's value format).
function isoDay(d) {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export default {
  name: "StoreCloseDateCard",
  components: { PortalButton },
  props: {
    // One /api/portal/stores row (see the header comment for its fields).
    store: { type: Object, required: true },
  },
  emits: ["updated"],
  data() {
    return {
      dialogOpen: false,
      newDate: "",
      saving: false,
      errorMsg: "",
      successMsg: "",
    };
  },
  computed: {
    actionShown() {
      return Boolean(this.store.can_extend_close_date || this.store.extend_blocked_reason);
    },
    daysLabel() {
      return this.store.days_until_close === 1 ? "day until close" : "days until close";
    },
    // The earliest date the picker offers: tomorrow on this browser's
    // calendar. A bare date is midnight, so today has already started. The
    // server re-checks on the distributor's clock and is the authority.
    minDate() {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      return isoDay(d);
    },
  },
  watch: {
    // A different store was scoped: drop the previous store's messages.
    "store.id"() {
      this.successMsg = "";
      this.errorMsg = "";
      this.dialogOpen = false;
    },
  },
  methods: {
    // The DATE part of the naive wall-clock ISO the server sends, read as
    // components so no timezone conversion can move it to another day.
    formatDate(iso) {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
      if (!m) return "";
      return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
    },
    openDialog() {
      if (!this.store.can_extend_close_date) return;
      const current = (this.store.close_date || "").slice(0, 10);
      this.newDate = current && current >= this.minDate ? current : this.minDate;
      this.errorMsg = "";
      this.successMsg = "";
      this.dialogOpen = true;
    },
    async confirm() {
      if (!this.newDate || this.saving) return;
      this.saving = true;
      this.errorMsg = "";
      try {
        const resp = await axios.post(`/api/portal/stores/${this.store.id}/end-date`, {
          end_date: this.newDate,
        });
        const data = (resp && resp.data) || {};
        const when = this.formatDate(data.store && data.store.close_date);
        if (data.unchanged) {
          // Same date as before: the server wrote and emailed nothing.
          this.successMsg = `The close date is already ${when}.`;
        } else {
          this.successMsg = data.notified
            ? `New close date: ${when}. ${data.notified} has been notified.`
            : `New close date: ${when}.`;
        }
        this.dialogOpen = false;
        if (data.store) this.$emit("updated", data.store);
      } catch (e) {
        const body = (e && e.response && e.response.data) || {};
        this.errorMsg =
          (typeof body.error === "string" && body.error) ||
          (typeof body.message === "string" && body.message) ||
          "Could not change the close date. Please try again.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.close-date-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--tf-sp-3, 12px);
  flex-wrap: wrap;
}
.close-date-card__action {
  max-width: 280px;
  text-align: right;
}
.close-date-card__blocked {
  color: var(--tf-fg-3, #616161);
}
.close-date-card__locked {
  color: var(--tf-fg-3, #616161);
}
.close-date-card__error {
  color: var(--tf-danger, #b00020);
}
.close-date-card__success {
  color: var(--tf-success, #1b5e20);
}
</style>
